import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import api from '../../services/api';

const EmailSettings = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [configs, setConfigs] = useState([]);

    const fetchConfigs = async () => {
        try {
            setLoading(true);
            setError(null);
            setSuccess(null);
            const response = await api.get('/admin/email/configs');
            setConfigs(response.data.configs);
        } catch (error) {
            setError('Failed to load email configurations');
        } finally {
            setLoading(false);
        }
    };

    // Fetch email configs from backend
    useEffect(() => {
        fetchConfigs();
    }, []);

    const handleGoogleAuth = async (config) => {
        try {
            setLoading(true);
            setError(null);

            // Get current auth token
            const authToken = localStorage.getItem('authToken');

            // Initiate OAuth flow
            const response = await api.post('/admin/email/google/init', {
                email: config.from,
                client_id: config.client_id,
                client_secret: config.client_secret,
                redirect_uri: config.redirect_uri
            });

            // Add auth token to the redirect URI
            const authUrl = new URL(response.data.authUrl);
            authUrl.searchParams.append('auth_token', authToken);

            // Try to open in popup
            const authWindow = window.open(
                authUrl.toString(),
                'Google OAuth',
                'width=600,height=600'
            );

            if (authWindow) {
                // Handle popup case
                const handleMessage = async (event) => {
                    if (event.origin !== window.location.origin) return;
                    
                    if (event.data.type === 'GOOGLE_OAUTH_CALLBACK') {
                        authWindow.close();
                        window.removeEventListener('message', handleMessage);

                        try {
                            // Complete OAuth setup
                            await api.post('/admin/email/google/callback', {
                                code: event.data.code,
                                email: config.from,
                                client_id: config.client_id,
                                client_secret: config.client_secret,
                                redirect_uri: config.redirect_uri
                            }, {
                                headers: {
                                    'Authorization': `Bearer ${event.data.authToken}`
                                }
                            });

                            setError(null);
                            setSuccess('Google OAuth setup completed successfully!');
                            // Refresh configs after successful setup
                            fetchConfigs();
                        } catch (error) {
                            setError('Failed to complete Google OAuth setup');
                        }
                    }
                };

                window.addEventListener('message', handleMessage);
            } else {
                setError('Popup was blocked. Please enable popups and try again.');
            }
        } catch (error) {
            setError('Failed to initiate Google OAuth setup');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                Email Settings
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Email Configurations
                    </Typography>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {configs.map((config, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{config.type}</TableCell>
                                        <TableCell>{config.from}</TableCell>
                                        <TableCell>
                                            {loading ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <Typography
                                                    color={config.status === 'configured' ? 'success.main' : 'warning.main'}
                                                >
                                                    {config.status === 'configured' ? 'Configured' : 'Requires Setup'}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {config.type === 'google' && config.status === 'requires_setup' && (
                                                <Button
                                                    variant="contained"
                                                    onClick={() => handleGoogleAuth(config)}
                                                    disabled={loading}
                                                >
                                                    Setup Google OAuth
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default EmailSettings; 